import { useEffect, useId, useRef, useState } from "react";
import { LANG_GROUPS, LANG_LABEL, LANG_SHORT } from "@b00k/domain";
import type { Lang } from "@b00k/domain";

/**
 * Wybór języka z listy rozwijanej.
 *
 * Przy dwóch językach wystarczał przełącznik `PL | EN`; przy dziewięciu zajmowałby
 * pół nagłówka i zmuszał do czytania wszystkich pozycji, żeby znaleźć jedną.
 * Lista rozwijana pokazuje tylko wybrany język, a resztę na żądanie.
 *
 * Grupy z `LANG_GROUPS` rozdziela LINIA — alfabet łaciński osobno, pismo
 * wschodnioazjatyckie osobno. Linia jest `role="separator"`, więc czytnik ekranu
 * ogłasza podział zamiast czytać dziewięć pozycji jednym ciągiem.
 *
 * Nazwy są w językach, których dotyczą: „Deutsch", nie „niemiecki". Kto szuka
 * swojego języka, szuka słowa, które zna — a nie zna go w cudzym.
 */
export function LangPicker({
  value,
  onChange,
  ariaLabel = "Język / Language",
}: {
  value: Lang;
  onChange: (l: Lang) => void;
  ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  // Zamknięcie kliknięciem obok i Escape: lista przykrywa treść, więc musi dać
  // się zamknąć bez wybierania czegokolwiek.
  useEffect(() => {
    if (!open) return;
    const poza = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", poza);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", poza);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  return (
    <div className="ui-lang" ref={boxRef}>
      <button
        type="button"
        className="ui-lang__btn"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{LANG_SHORT[value]}</span>
        <svg width="10" height="6" viewBox="0 0 10 6" aria-hidden="true">
          <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
      {open && (
        <ul className="ui-lang__list" id={listId} role="listbox" aria-label={ariaLabel}>
          {LANG_GROUPS.map((grupa, i) => (
            <li key={grupa.join()} className="ui-lang__group">
              {i > 0 && <hr className="ui-lang__sep" role="separator" />}
              <ul role="none">
                {grupa.map((l) => (
                  <li key={l} role="none">
                    <button
                      type="button"
                      role="option"
                      aria-selected={l === value}
                      className={`ui-lang__opt${l === value ? " is-active" : ""}`}
                      lang={l}
                      onClick={() => {
                        onChange(l);
                        setOpen(false);
                      }}
                    >
                      {LANG_LABEL[l]}
                    </button>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
